import React, { useCallback, useEffect, useRef } from 'react';
import { DownloadIcon } from '@rsos/assets/icons';
import { ExportButtonContainer } from './ExportMapButton.styles';
import type { ExportMapButtonProps, MapView } from './ExportMapButton.types';

const ExportMapButton: React.FC<ExportMapButtonProps> = ({
  mapInstance,
  onExportStart,
  onExportComplete,
  isVisible,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(async (): Promise<void> => {
    if (!mapInstance?.view) {
      // eslint-disable-next-line no-console
      console.error('Map view not available');
      return;
    }

    try {
      onExportStart?.();

      const view: MapView = mapInstance.view;

      await view.when();

      const maxDimension = 2048; // Reduced for better compatibility
      const originalWidth = view.width;
      const originalHeight = view.height;

      let exportWidth = originalWidth;
      let exportHeight = originalHeight;

      if (originalWidth > maxDimension || originalHeight > maxDimension) {
        const scale = Math.min(
          maxDimension / originalWidth,
          maxDimension / originalHeight,
        );
        exportWidth = Math.floor(originalWidth * scale);
        exportHeight = Math.floor(originalHeight * scale);
      }

      const screenshot = await view.takeScreenshot({
        format: 'png',
        width: exportWidth,
        height: exportHeight,
      });

      const link = document.createElement('a');
      link.download = `map-export-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
      link.href = screenshot.dataUrl;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onExportComplete?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error exporting map:', error);

      onExportComplete?.();
    }
  }, [mapInstance, onExportStart, onExportComplete]);

  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const appendButton = (): void => {
        const sketchPanel = document.querySelector('.esri-sketch__panel');

        if (
          sketchPanel &&
          buttonRef.current &&
          !sketchPanel.contains(buttonRef.current)
        ) {
          sketchPanel.appendChild(buttonRef.current);
        } else if (!sketchPanel) {
          // If panel doesn't exist yet, try again in a short delay
          setTimeout(appendButton, 100);
        }
      };

      appendButton();
    }

    return () => {
      if (buttonRef.current && buttonRef.current.parentNode) {
        buttonRef.current.parentNode.removeChild(buttonRef.current);
      }
    };
  }, [isVisible]);

  return (
    <ExportButtonContainer
      ref={buttonRef}
      onClick={handleExport}
      isVisible={isVisible}
      title="Export Map as Image"
    >
      <DownloadIcon
        viewboxHeight={12}
        viewboxWidth={8}
        width={16}
        height={16}
        color="primaryText"
      />
    </ExportButtonContainer>
  );
};

export default ExportMapButton;
